import { CropChip, NotObservedChip, useFigures } from '@cibo/ui'
import {
  Box,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { LandReportBaseComponentProps } from '../types'
import { useTranslation } from 'react-i18next'
import { UseQueryResult } from '@tanstack/react-query'
import { FieldHistoryResult, MpxResponseError } from '@cibo/core'

const StyledTableCell = styled(TableCell)(() => ({
  whiteSpace: 'nowrap',
}))

interface CarbonFootprintTableProps extends LandReportBaseComponentProps {
  fieldHistory: UseQueryResult<FieldHistoryResult | undefined, MpxResponseError>
}

export const CarbonFootprintTable = ({
  field,
  focusYear,
  fieldHistory,
}: CarbonFootprintTableProps) => {
  const { t } = useTranslation('@cibo/landmanager/LandReport/CarbonFootprintTable')
  const { cropYield } = useFigures()

  if (!!fieldHistory.data && !!field.data) {
    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell>{t('year')}</StyledTableCell>
              <StyledTableCell>{t('cashCrop')}</StyledTableCell>
              <StyledTableCell>{t('intensity')}</StyledTableCell>
              <StyledTableCell>{t('emissions')}</StyledTableCell>
              <StyledTableCell>{t('dieselUse')}</StyledTableCell>
              <StyledTableCell>{t('soilFlux')}</StyledTableCell>
              <StyledTableCell>{t('n2OSoilFlux')}</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {field.data.reportYears.map(year => {
              const historicalYield = fieldHistory?.data?.historicalYields.find(
                c => c.year === year
              )

              return (
                <TableRow key={year} selected={year === focusYear}>
                  <StyledTableCell>{year}</StyledTableCell>
                  <StyledTableCell>
                    <Stack>
                      <Box>
                        {!!historicalYield ? (
                          <CropChip crop={historicalYield.crop} source="cibo" />
                        ) : (
                          <NotObservedChip />
                        )}
                      </Box>
                      <Typography color="text.secondary" variant="caption">
                        {!!historicalYield
                          ? cropYield({
                              cropYield: historicalYield.yield,
                              crop: historicalYield.crop,
                            })
                          : '--'}
                      </Typography>
                    </Stack>
                  </StyledTableCell>
                  <StyledTableCell>
                    {!!historicalYield?.ciboFootprint?.results?.intensity ? (
                      <Stack>
                        <Typography variant="body2">
                          {t('emissionsTotal', {
                            amt:
                              historicalYield.ciboFootprint.results.intensity *
                              historicalYield.ciboFootprint.results.acreage,
                          })}
                        </Typography>
                        <Typography variant="caption">
                          {t('emissionsPerAcre', {
                            amt: historicalYield.ciboFootprint.results.total,
                          })}
                        </Typography>
                      </Stack>
                    ) : (
                      '--'
                    )}
                  </StyledTableCell>
                  <StyledTableCell>
                    {!!historicalYield?.ciboFootprint?.results?.total ? (
                      <Stack>
                        <Typography variant="body2">
                          {t('emissionsTotal', {
                            amt:
                              historicalYield.ciboFootprint.results.total *
                              historicalYield.ciboFootprint.results.acreage,
                          })}
                        </Typography>
                        <Typography variant="caption">
                          {t('emissionsPerAcre', {
                            amt: historicalYield.ciboFootprint.results.total,
                          })}
                        </Typography>
                      </Stack>
                    ) : (
                      '--'
                    )}
                  </StyledTableCell>
                  <StyledTableCell>
                    {!!historicalYield?.ciboFootprint?.results?.co2Diesel ? (
                      <Stack>
                        <Typography variant="body2">
                          {t('emissionsTotal', {
                            amt:
                              historicalYield.ciboFootprint.results.co2Diesel *
                              historicalYield.ciboFootprint.results.acreage,
                          })}
                        </Typography>
                        <Typography variant="caption">
                          {t('emissionsPerAcre', {
                            amt: historicalYield.ciboFootprint.results.co2Diesel,
                          })}
                        </Typography>
                      </Stack>
                    ) : (
                      '--'
                    )}
                  </StyledTableCell>
                  <StyledTableCell>
                    {!!historicalYield?.ciboFootprint?.results?.co2Soils ? (
                      <Stack>
                        <Typography variant="body2">
                          {t('emissionsTotal', {
                            amt:
                              historicalYield.ciboFootprint.results.co2Soils *
                              historicalYield.ciboFootprint.results.acreage,
                          })}
                        </Typography>
                        <Typography variant="caption">
                          {t('emissionsPerAcre', {
                            amt: historicalYield.ciboFootprint.results.co2Soils,
                          })}
                        </Typography>
                      </Stack>
                    ) : (
                      '--'
                    )}
                  </StyledTableCell>
                  <StyledTableCell>
                    {!!historicalYield?.ciboFootprint?.results?.n2oSoils ? (
                      <Stack>
                        <Typography variant="body2">
                          {t('emissionsTotal', {
                            amt:
                              historicalYield.ciboFootprint.results.n2oSoils *
                              historicalYield.ciboFootprint.results.acreage,
                          })}
                        </Typography>
                        <Typography variant="caption">
                          {t('emissionsPerAcre', {
                            amt: historicalYield.ciboFootprint.results.n2oSoils,
                          })}
                        </Typography>
                      </Stack>
                    ) : (
                      '--'
                    )}
                  </StyledTableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }
  return null
}
